import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { moduloAPI, empresaAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
    FileText,
    Package,
    BarChart3,
    Users,
    Settings,
    LogOut,
    Sun,
    Moon,
    Building2,
    Lock,
    ChevronRight,
    Flame,
    UserCog,
    ClipboardList,
    Fuel,
    Shield,
    Satellite,
} from 'lucide-react';

// Map icon names to components
const iconMap = {
    FileText,
    Package,
    BarChart3,
    Users,
    Settings,
    Flame,
    ClipboardList,
    Fuel,
    Shield,
    Satellite,
    Radio: Satellite,
};

// Module route mapping
const moduleRoutes = {
    guias_despacho: '/guias/dashboard',
    bomberos: '/bomberos/dashboard',
    ordenes_servicio: '/ordenes/dashboard',
    diesel: '/diesel/dashboard',
    alarmas_ls: '/alarmas/panel',
    integrador_codelco: '/codelco/dashboard',
    gestion_4sat: '/gestion4sat/dashboard',
    inventario: '/inventario',
    reportes: '/reportes',
    personal: '/personal',
};

export default function HomePage() {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const { theme, toggleTheme, isDark } = useTheme();
    const [modulos, setModulos] = useState([]);
    const [allModulos, setAllModulos] = useState([]);
    const [empresa, setEmpresa] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Si usuario tiene acceso a un solo módulo, redirigir directamente
        if (user?.modulos_acceso?.length === 1) {
            const route = moduleRoutes[user.modulos_acceso[0]];
            if (route) {
                navigate(route, { replace: true });
                return;
            }
        }
        loadData();
    }, [user]);

    const loadData = async () => {
        try {
            // Get modules assigned to user's company
            const modulosRes = await moduloAPI.getEmpresa();
            setModulos(modulosRes.data);

            // Get all modules to show locked ones
            const allModulosRes = await moduloAPI.getAll();
            setAllModulos(allModulosRes.data);

            // Get company info
            if (user?.empresa_id) {
                const empresaRes = await empresaAPI.getById(user.empresa_id);
                setEmpresa(empresaRes.data);
            }

            // Security role goes directly to all guides list if guias_despacho is available
            if (user?.rol === 'seguridad' && modulosRes.data.some(m => m.codigo === 'guias_despacho')) {
                navigate('/guias/todos', { replace: true });
                return;
            }

            // If only one module, redirect directly (for other roles)
            if (modulosRes.data.length === 1 && user?.rol !== 'superadmin') {
                const route = moduleRoutes[modulosRes.data[0].codigo];
                if (route) {
                    navigate(route, { replace: true });
                    return;
                }
            }
        } catch (err) {
            console.error('Error loading modules:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleModuleClick = (modulo) => {
        const assignedCodes = modulos.map(m => m.codigo);
        if (!assignedCodes.includes(modulo.codigo)) {
            toast.error('Módulo no disponible', {
                description: 'Contacte al administrador para habilitar este módulo.',
            });
            return;
        }

        const route = moduleRoutes[modulo.codigo];
        if (route) {
            navigate(route);
        } else {
            toast.info('Módulo en desarrollo', {
                description: 'Este módulo estará disponible próximamente.',
            });
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const assignedCodes = modulos.map(m => m.codigo);

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-sm border-b-2 border-border h-16">
                <div className="max-w-6xl mx-auto h-full px-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-sm bg-primary flex items-center justify-center">
                            <Building2 className="h-6 w-6 text-primary-foreground" />
                        </div>
                        <div>
                            <h1 className="font-heading font-black text-xl uppercase tracking-tight">
                                Multisistemas
                            </h1>
                            <p className="text-xs text-muted-foreground">
                                {empresa?.nombre || 'Plataforma de Módulos'}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {(user?.rol === 'admin' || user?.rol === 'superadmin') && (
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => navigate('/gestion-usuarios')}
                                title="Gestión de Usuarios"
                                data-testid="users-btn"
                            >
                                <UserCog className="h-5 w-5" />
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            data-testid="theme-toggle"
                        >
                            {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>
                        <div className="hidden md:block text-right mr-2">
                            <p className="text-sm font-medium">{user?.nombre}</p>
                            <p className="text-xs text-muted-foreground capitalize">{user?.rol}</p>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleLogout}
                            className="border-2"
                            data-testid="logout-btn"
                        >
                            <LogOut className="h-4 w-4 mr-2" />
                            Salir
                        </Button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-24 pb-12 px-4">
                <div className="max-w-6xl mx-auto">
                    {/* Welcome */}
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight mb-2">
                            Bienvenido, {user?.nombre?.split(' ')[0]}
                        </h2>
                        <p className="text-muted-foreground">
                            Selecciona un módulo para comenzar
                        </p>
                    </div>

                    {/* Modules Grid */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {[1, 2, 3, 4].map((i) => (
                                <Skeleton key={i} className="h-48" />
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {allModulos.map((modulo) => {
                                const Icon = iconMap[modulo.icono] || Package;
                                const isAvailable = assignedCodes.includes(modulo.codigo);
                                
                                return (
                                    <Card
                                        key={modulo.id}
                                        className={`border-2 cursor-pointer transition-all duration-200 ${
                                            isAvailable
                                                ? 'hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,0.1)] hover:border-primary'
                                                : 'opacity-60 cursor-not-allowed'
                                        }`}
                                        onClick={() => handleModuleClick(modulo)}
                                        data-testid={`module-${modulo.codigo}`}
                                    >
                                        <CardContent className="p-6">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className={`p-4 rounded-sm ${isAvailable ? 'bg-primary/10' : 'bg-muted'}`}>
                                                    <Icon className={`h-8 w-8 ${isAvailable ? 'text-primary' : 'text-muted-foreground'}`} />
                                                </div>
                                                {!isAvailable && (
                                                    <div className="p-2 bg-muted rounded-sm">
                                                        <Lock className="h-4 w-4 text-muted-foreground" />
                                                    </div>
                                                )}
                                            </div>
                                            <h3 className="font-heading font-bold text-lg uppercase tracking-tight mb-2">
                                                {modulo.nombre}
                                            </h3>
                                            <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                                                {modulo.descripcion}
                                            </p>
                                            {isAvailable ? (
                                                <div className="flex items-center text-primary text-sm font-medium">
                                                    <span>Acceder</span>
                                                    <ChevronRight className="h-4 w-4 ml-1" />
                                                </div>
                                            ) : (
                                                <p className="text-xs text-muted-foreground">
                                                    Módulo no disponible
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>
                    )}

                    {/* SuperAdmin Link */}
                    {user?.rol === 'superadmin' && (
                        <div className="mt-12 text-center">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/superadmin')}
                                className="border-2"
                                data-testid="superadmin-btn"
                            >
                                <Settings className="h-4 w-4 mr-2" />
                                Panel de Super Administrador
                            </Button>
                        </div>
                    )}

                    {/* Admin Config Link */}
                    {user?.rol === 'admin' && (
                        <div className="mt-12 text-center">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    const configRoutes = {
                                        gestion_4sat: '/gestion4sat/config',
                                        guias_despacho: '/guias/configuracion',
                                        bomberos: '/bomberos/configuracion',
                                        ordenes_servicio: '/ordenes/configuracion',
                                        diesel: '/diesel/configuracion',
                                        alarmas_ls: '/alarmas/configuracion',
                                        integrador_codelco: '/codelco/config',
                                    };
                                    // Use user's modulos_acceso first, fallback to loaded company modules
                                    const userModulos = user?.modulos_acceso?.length > 0
                                        ? user.modulos_acceso
                                        : modulos.map(m => m.codigo);
                                    const firstModule = userModulos[0];
                                    const route = configRoutes[firstModule] || '/home';
                                    navigate(route);
                                }}
                                className="border-2"
                                data-testid="config-btn"
                            >
                                <Settings className="h-4 w-4 mr-2" />
                                Configuración de Empresa
                            </Button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
}
